import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  syncManager, 
  SyncEvent, 
  SyncResult 
} from '@/lib/offline/sync-manager';
import {
  getPendingCount,
  getFailedActions,
  getAllActions,
  retryAction as retryQueueAction,
  discardAction as discardQueueAction,
  PendingAction,
} from '@/lib/offline/action-queue';
import { 
  resolveConflict as resolveQueueConflict, 
  ConflictInfo,
  ConflictResolution,
} from '@/lib/offline/conflict-resolver';
import { queryKeys } from '@/lib/query-keys';

export interface UseOfflineSyncResult {
  pendingCount: number;
  isSyncing: boolean;
  currentConflict: ConflictInfo | null;
  currentConflictAction: PendingAction | null;
  failedActions: PendingAction[];
  lastSyncResult: SyncResult | null;
  resolveConflict: (resolution: ConflictResolution) => Promise<void>;
  retryAction: (actionId: string) => Promise<void>;
  discardAction: (actionId: string) => Promise<void>;
  forceSync: () => Promise<void>;
}

export function useOfflineSync(): UseOfflineSyncResult {
  const queryClient = useQueryClient();
  
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentConflict, setCurrentConflict] = useState<ConflictInfo | null>(null);
  const [currentConflictAction, setCurrentConflictAction] = useState<PendingAction | null>(null);
  const [failedActions, setFailedActions] = useState<PendingAction[]>([]);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);

  // Initialize sync manager and load initial state
  useEffect(() => {
    syncManager.init();

    // Load initial counts
    const loadInitialState = async () => {
      const count = await getPendingCount();
      setPendingCount(count);
      
      const failed = await getFailedActions();
      setFailedActions(failed);
    };
    loadInitialState();

    // Subscribe to sync events
    const unsubscribe = syncManager.subscribe((event: SyncEvent) => {
      switch (event.type) {
        case 'sync_start':
          setIsSyncing(true);
          break;
          
        case 'sync_complete':
          setIsSyncing(false);
          setLastSyncResult(event.result);
          // Refresh failed actions
          getFailedActions().then(setFailedActions);
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.all });
          queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.installations() });
          queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.claimedPickups() });
          break;
          
        case 'pending_count_changed':
          setPendingCount(event.count);
          break;
          
        case 'conflict_detected':
          setCurrentConflict(event.conflict);
          // Find the action for this conflict
          getAllActions().then(actions => {
            const action = actions.find(a => a.id === event.conflict.actionId);
            setCurrentConflictAction(action || null);
          });
          break;
          
        case 'action_failed':
          // Refresh failed actions list
          getFailedActions().then(setFailedActions);
          break;
      }
    });

    return unsubscribe;
  }, [queryClient]);

  // Resolve a conflict
  const resolveConflict = useCallback(async (resolution: ConflictResolution) => {
    if (!currentConflict) return;
    
    await resolveQueueConflict(currentConflict.actionId, resolution, currentConflictAction || undefined);
    setCurrentConflict(null);
    setCurrentConflictAction(null);
    
    // Re-trigger sync if we chose to overwrite
    if (resolution === 'overwrite') {
      await syncManager.forceSync();
    }
    
    // Update pending count
    const count = await getPendingCount();
    setPendingCount(count);
  }, [currentConflict, currentConflictAction]);

  // Retry a failed action
  const retryAction = useCallback(async (actionId: string) => {
    await retryQueueAction(actionId);
    
    // Update states
    const failed = await getFailedActions();
    setFailedActions(failed);
    const count = await getPendingCount();
    setPendingCount(count);
    
    // Trigger sync
    await syncManager.forceSync();
  }, []);

  // Discard a failed action
  const discardAction = useCallback(async (actionId: string) => {
    await discardQueueAction(actionId);
    
    // Update states
    const failed = await getFailedActions();
    setFailedActions(failed);
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  // Force sync
  const forceSync = useCallback(async () => {
    await syncManager.forceSync();
  }, []);

  return {
    pendingCount,
    isSyncing,
    currentConflict,
    currentConflictAction,
    failedActions,
    lastSyncResult,
    resolveConflict,
    retryAction,
    discardAction,
    forceSync,
  };
}
